import React from 'react';
import { CustomInput } from 'reactstrap';
import { auth } from '../../Api';
import SiteLayout from '../../components/site/SiteLayout';
import { clearStoredLogin, setTermsRequired } from '../../utils/session';
import { formatServerMessage } from '../../utils/serverErrors';

/**
 * The Terms and Conditions page (/accept-terms; laravel TermsController, App/Terms.php): a company admin whose company
 * has not accepted the current version sees it after signing in, before anything else, and cannot use the admin
 * website until they tick the box and accept (the server refuses everything else with code 428). District managers,
 * resellers and the super admin are never asked. In the Mobiliis design, inside the main website's header and footer.
 */
class AcceptTerms extends React.Component {
	constructor(props) {
		super(props);
		this.state = { terms: null, agreed: false, problem: null, busy: false, loadError: false };
	}

	componentDidMount() {
		this.load();
	}

	load() {
		auth.getCurrentTerms()
			.then((res) => {
				const data = res && res.data;
				if (!data || data.code != 200) {
					this.setState({ loadError: true });
					return;
				}
				if (!data.must_accept || !data.terms) {
					this.carryOn();
					return;
				}
				this.setState({ terms: data.terms, loadError: false });
			})
			.catch(() => this.setState({ loadError: true }));
	}

	carryOn() {
		setTermsRequired(false);
		window.location.assign('/company-dashboard');
	}

	accept() {
		const { terms, agreed } = this.state;
		if (!agreed) {
			this.setState({ problem: 'Please tick the box to agree to the Terms and Conditions.' });
			return;
		}
		this.setState({ busy: true, problem: null });
		auth.acceptTerms({ terms_version_id: terms.id, accepted: true })
			.then((res) => {
				const data = res && res.data;
				if (data && data.code == 200) {
					this.carryOn();
					return;
				}
				// A newer version was published meanwhile: show it, the box unticked
				if (data && data.terms) {
					this.setState({ terms: data.terms, agreed: false });
				}
				this.setState({ busy: false, problem: formatServerMessage(data ? data.message : null, 'It could not be saved. Please try again.') });
			})
			.catch(() => this.setState({ busy: false, problem: 'Could not reach the server. Please try again.' }));
	}

	signOut() {
		auth.adminLogout().catch(() => {});
		clearStoredLogin();
		window.location.assign('/authentication/login2');
	}

	render() {
		const { terms, agreed, problem, busy, loadError } = this.state;
		return (
			<SiteLayout page="terms">
				<div className="mob-narrow">
					<h1 className="mob-title mb-3">Terms and Conditions</h1>
					<div className="mob-card">
						{loadError && (
							<div className="mob-message mob-message-error" role="alert">
								The Terms and Conditions could not be loaded.{' '}
								<button type="button" className="mob-link-button" onClick={() => this.load()}>Try again</button>
							</div>
						)}
						{!terms && !loadError && <p className="mb-0">Loading...</p>}
						{terms && (
							<React.Fragment>
								<p>
									Before you carry on, please read the Mobiliis Terms and Conditions (version {terms.version}) and accept them for
									your company.
								</p>
								<p>
									<a href={terms.url} target="_blank" rel="noopener noreferrer">Read the Terms and Conditions</a>
								</p>
								<CustomInput type="checkbox" id="accept-terms-box" checked={agreed} className="mb-3"
									onChange={(e) => this.setState({ agreed: e.target.checked, problem: null })}
									label={<span>I agree to the <a href={terms.url} target="_blank" rel="noopener noreferrer">Terms and Conditions</a></span>} />
								{problem && <div className="mob-message mob-message-error" role="alert">{problem}</div>}
								<button type="button" className="mob-btn mob-btn-primary mob-btn-block" disabled={busy || !agreed} onClick={() => this.accept()}>
									{busy ? 'Saving...' : 'Accept'}
								</button>
							</React.Fragment>
						)}
						<p className="text-center mt-3 mb-0">
							<button type="button" className="mob-link-button" onClick={() => this.signOut()}>Sign out</button>
						</p>
					</div>
				</div>
			</SiteLayout>
		);
	}
}

export default AcceptTerms;
